import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '../components/ui/skeleton';
import TrendingSection from '../components/trending/TrendingSection';
import TopTopicsBlock from '../components/trending/TopTopicsBlock';
import HeroImageSlideshow from '../components/layout/HeroImageSlideshow';
import { Layers } from 'lucide-react';
import { slugify } from '../lib/slugify';
import {
  useHowItWorksTopics,
  groupTopicsByCategory,
  topicToTrendingItem,
  TOP_TOPICS_N,
} from '../lib/howItWorksHierarchy';

const HowItWorksPage = () => {
  const { topics: howItWorksTopics, loading } = useHowItWorksTopics();

  const trendingTopicItems = useMemo(
    () => howItWorksTopics.map(topicToTrendingItem),
    [howItWorksTopics]
  );

  const categories = useMemo(() => groupTopicsByCategory(howItWorksTopics), [howItWorksTopics]);

  return (
    <div className="min-h-screen" data-testid="how-it-works-page">
      {/* Hero Section */}
      <section className="bg-chassis text-white py-16 md:py-24">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-6">
              <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-4">
                Automotive Knowledge Platform
              </p>
              <h1 className="font-chivo text-4xl md:text-6xl font-black uppercase tracking-tighter mb-6">
                How Does It Work?
              </h1>
              <p className="text-lg text-white/70 max-w-xl leading-relaxed">
                Quick, visual breakdowns of automotive systems — what they are, where they live in the vehicle, and how they actually work — without the full engineering deep-dive.
              </p>
            </div>
            <div className="md:col-span-6 hidden md:block self-stretch">
              <HeroImageSlideshow alt="Automotive Systems" />
            </div>
          </div>
        </div>
      </section>

      <TrendingSection sectionType="how_it_works" items={trendingTopicItems} testId="how-it-works-all" />

      {/* Sections */}
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="how-it-works-loading">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 w-full" />
              ))}
            </div>
          ) : howItWorksTopics.length === 0 ? (
            <div className="text-center py-16" data-testid="no-topics-message">
              <Layers className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-chivo text-xl font-bold uppercase mb-2">No Topics Yet</h3>
              <p className="text-muted-foreground">Check back soon for new system breakdowns.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {categories.map(([category, subGroups]) => {
                const categorySlug = slugify(category);
                const categoryTopics = subGroups.flatMap(([, topics]) => topics);
                const categoryItems = categoryTopics.map(topicToTrendingItem);
                const hasMore = categoryTopics.length > TOP_TOPICS_N;
                return (
                  <div key={category} data-testid={`how-it-works-category-${categorySlug}`}>
                    <div className="flex items-center justify-between mb-4">
                      <Link to={`/how-it-works/section/${categorySlug}`} className="group">
                        <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight group-hover:text-autoxium-blue transition-colors">
                          {category}
                        </h2>
                      </Link>
                      <p className="font-mono text-sm text-muted-foreground">
                        {categoryTopics.length} topic{categoryTopics.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <TopTopicsBlock
                      sectionType="how_it_works"
                      items={categoryItems}
                      hasMore={hasMore}
                      moreTopicsPath={`/how-it-works/section/${categorySlug}/more`}
                      testId={`how-it-works-${categorySlug}`}
                      size="sm"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
